// 24-hour Meta customer-service session window helpers — frontend
// twin of the backend's ConversationService.getSessionStatus.

const MS_HOUR = 3_600_000;

export interface SessionStatus {
  isOpen: boolean;
  remainingMs: number;
  remainingHours: number;
  remainingMinutes: number;
  /** True iff a non-template send would be rejected by Meta. */
  mustUseTemplate: boolean;
  /** Color hint for the badge: 'green' (>3h) | 'yellow' (1-3h) | 'red' (<1h or closed). */
  color: 'green' | 'yellow' | 'red';
  /** Short label, e.g. "23h 12m left" or "Closed". */
  label: string;
}

export function computeSession(
  lastInboundAt: string | null | undefined,
  sessionExpiresAt: string | null | undefined,
): SessionStatus {
  if (!lastInboundAt) {
    return {
      isOpen: false,
      remainingMs: 0,
      remainingHours: 0,
      remainingMinutes: 0,
      mustUseTemplate: true,
      color: 'red',
      label: 'No reply yet',
    };
  }

  const expiresAt = sessionExpiresAt
    ? new Date(sessionExpiresAt).getTime()
    : new Date(lastInboundAt).getTime() + 24 * MS_HOUR;
  const remainingMs = Math.max(0, expiresAt - Date.now());
  const isOpen = remainingMs > 0;
  const remainingHours = Math.floor(remainingMs / MS_HOUR);
  const remainingMinutes = Math.floor((remainingMs % MS_HOUR) / 60_000);

  if (!isOpen) {
    return {
      isOpen: false,
      remainingMs: 0,
      remainingHours: 0,
      remainingMinutes: 0,
      mustUseTemplate: true,
      color: 'red',
      label: 'Window closed',
    };
  }

  const color: SessionStatus['color'] =
    remainingHours >= 3 ? 'green' : remainingHours >= 1 ? 'yellow' : 'red';

  const label =
    remainingHours > 0
      ? `${remainingHours}h ${remainingMinutes}m left`
      : `${remainingMinutes}m left`;

  return {
    isOpen: true,
    remainingMs,
    remainingHours,
    remainingMinutes,
    mustUseTemplate: false,
    color,
    label,
  };
}
